// z25 (Diego, 20-oct-2026; caso SX-2026-0014): «que la IA corrija y modifique los consumos según pida el que está
// cotizando». Casa en construcción, meses en que no vivían, equipos nuevos: la IA PROPONE el consumo nuevo de cada
// periodo que cambia, con por qué y sus supuestos; el vendedor ve la tabla antes/después y la aplica. Aquí vive lo que
// comparten la IA (real y simulada), el servidor y la pantalla: la revisión de la propuesta (topes), las ediciones que
// la aplican, el texto del historial y la regla SIN IA (para la demo y las pruebas).
import { numero } from './dinero.ts';
import type { ContenidoCotizacion } from './dominio/cotizacion.ts';
import type { EdicionCampo } from './dominio/edicion.ts';
import type { AjusteConsumosPropuesto, MesAjustado } from './dominio/ia.ts';
import type { AjusteConsumoVendedor, LecturaRecibo, PeriodoConsumo, PeriodoDelRecibo } from './dominio/recibo.ts';
import { ventanaDelAnio } from './dominio/recibo.ts';

/** Fracción del consumo anual de una CASA en Sonora por mes (ENE…DIC; suma 1). Copia de paquetes/motor/datos/
 *  consumo-perfiles.json (perfil medido; 84 % cae de abril a octubre por los aires): el motor TRUENA al cargar si dejan
 *  de coincidir (datos.ts), para que no se separen en silencio. Los negocios van parejos (1/12 por mes), como el motor. */
export const PERFIL_CONSUMO_CASA: readonly number[] = Object.freeze([0.023, 0.0252, 0.0425, 0.0737, 0.0905, 0.1279, 0.1546, 0.1611, 0.1346, 0.0958, 0.0485, 0.0226]);
const PERFIL_NEGOCIO: readonly number[] = Object.freeze(Array.from({ length: 12 }, () => 1 / 12));

/** Tope de un periodo ajustado: 3 veces el periodo más alto del historial (como lo trajo el recibo) y nunca menos de
 *  3,000 kWh por mes del periodo (una casa grande con carro eléctrico en pleno verano cabe; un cero de más, no). */
export const TOPE_AJUSTE_VECES = 3;
export const TOPE_AJUSTE_MINIMO_KWH_MES = 3_000;

const MESES = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'] as const;
const NOMBRES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'] as const;
const DIA_MS = 86_400_000;
const dia = (f: string) => Date.UTC(Number(f.slice(0, 4)), Number(f.slice(5, 7)) - 1, Number(f.slice(8, 10))) / DIA_MS;
const clave = (anio: number, mes0: number) => `${anio}-${String(mes0 + 1).padStart(2, '0')}`;
const kwh = (x: number) => `${numero(x)} kWh`;

/** Mes calendario (AAAA-MM) de un periodo: el de su punto medio; sin fechas, el último mes de su etiqueta («JUN 26»). */
export function mesDelPeriodo(p: Pick<PeriodoConsumo, 'inicio' | 'fin' | 'etiqueta'>): string | null {
  if (p.inicio && p.fin) {
    const d = new Date(((dia(p.inicio) + dia(p.fin)) / 2) * DIA_MS);
    return clave(d.getUTCFullYear(), d.getUTCMonth());
  }
  const todas = [...p.etiqueta.toUpperCase().matchAll(/\b(ENE|FEB|MAR|ABR|MAY|JUN|JUL|AGO|SEP|OCT|NOV|DIC)\.?\s*(\d{2}|\d{4})\b/g)];
  const e = todas.at(-1);
  if (e) {
    const a = Number(e[2]);
    return clave(a < 100 ? 2000 + a : a, MESES.indexOf(e[1] as (typeof MESES)[number]));
  }
  return p.fin ? p.fin.slice(0, 7) : null;
}

/** Peso del periodo en el año según el perfil (suma por día de la fracción de su mes); sin fechas, por su etiqueta. */
function pesoDelPeriodo(p: PeriodoConsumo, perfil: readonly number[], bimestral: boolean): number | null {
  if (p.inicio && p.fin && dia(p.fin) > dia(p.inicio)) {
    let s = 0;
    for (let d = dia(p.inicio); d < dia(p.fin); d++) {
      const x = new Date(d * DIA_MS);
      const diasMes = new Date(Date.UTC(x.getUTCFullYear(), x.getUTCMonth() + 1, 0)).getUTCDate();
      s += perfil[x.getUTCMonth()]! / diasMes;
    }
    return s;
  }
  const k = mesDelPeriodo(p);
  if (!k) return null;
  const m = Number(k.slice(5, 7)) - 1;
  return bimestral ? perfil[m]! + perfil[(m + 11) % 12]! : perfil[m]!;
}

const perfilDe = (c: ContenidoCotizacion) => (c.cuestionario.tipo === 'comercio' || c.cuestionario.tipo === 'industria' ? PERFIL_NEGOCIO : PERFIL_CONSUMO_CASA);

/** 21-oct (SX-2026-0014): el periodo salió de un recibo o de su historial impreso, aunque después se le hayan ajustado los
 *  kWh (cuenta el origen que traía del recibo). Una lectura con alguno así «vino de recibo(s)»; una capturada a mano desde
 *  cero no tiene ninguno. La usan el motor y la tabla de Revisión (un solo criterio). */
export function vieneDelRecibo(p: PeriodoConsumo): boolean {
  const o = p.ajuste_vendedor?.recibo?.origen ?? p.origen;
  return o === 'recibo' || o === 'historial_recibo';
}

const centavosDe = (x: number) => Math.round(x * 100);
const conValor = (x: number | null | undefined): x is number => x !== null && x !== undefined && Number.isFinite(x);
const etiquetaNormal = (e: string) => e.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ').trim().toUpperCase();

/** hoy85 (verificador de hoy84): a qué periodo de ANTES corresponde cada renglón de DESPUÉS (índice en `previa`, o null si es un periodo nuevo). Uno a
 *  uno y en este orden: mismas fechas; mismo corte (el alta que el cotizador toma como el periodo completo: cambia su inicio);
 *  misma etiqueta (un renglón sin fechas, o una fecha mal leída que se corrige); y un renglón que NO tenía fechas y ahora sí
 *  («Poner los meses que faltan» lo pone en su lugar con otra etiqueta), por el importe que traía. Lo usan la regla del importe
 *  (importes-del-recibo.ts) y la marca de kWh editados (ediciones.ts): UNA forma de reconocer el periodo.
 *  Verificadores de hoy85 (regresión contra producción): cuando la IA corrige las FECHAS de un periodo mal leído (su fin y su
 *  etiqueta, con su MISMO importe impreso), ninguna de esas tres claves empata y el importe se tomaba por «escrito a mano» (400 al
 *  aplicar, y /corregir quitaba la propuesta en silencio). Dos arreglos:
 *   · `porIndice`: las ediciones son celda por celda o renglón por renglón (`/historial/3/fin`, `/historial/3`) y el historial no
 *     cambió de largo: el renglón 3 ES el periodo 3 (como el porIndice de marcarConsumosEditados). Quien llama lo decide con las
 *     ediciones, que es lo único que dice si un renglón se editó en su lugar;
 *   · con el historial o la lectura ENTEROS (otro largo u otro orden): también por su importe Y sus kWh juntos (el mismo renglón del
 *     recibo con otras fechas). Un importe de otro periodo, o con otros kWh, sigue sin empatar. */
export function periodosDeAntes(previa: readonly PeriodoConsumo[], nueva: readonly PeriodoConsumo[], op: { porIndice: boolean }): (number | null)[] {
  if (op.porIndice && previa.length === nueva.length) return nueva.map((_, i) => i);
  const out: (number | null)[] = nueva.map(() => null);
  const usados = new Set<number>();
  const pasar = (igual: (n: PeriodoConsumo, p: PeriodoConsumo) => boolean, solo?: (n: PeriodoConsumo) => boolean) => {
    nueva.forEach((n, i) => {
      if (out[i] !== null || (solo && !solo(n))) return;
      const j = previa.findIndex((p, k) => !usados.has(k) && igual(n, p));
      if (j < 0) return;
      usados.add(j);
      out[i] = j;
    });
  };
  pasar((n, p) => !!n.inicio && !!n.fin && n.inicio === p.inicio && n.fin === p.fin);
  pasar((n, p) => !!n.fin && n.fin === p.fin);
  pasar((n, p) => etiquetaNormal(n.etiqueta) === etiquetaNormal(p.etiqueta));
  const delPeriodo = (p: PeriodoConsumo) => [p.importe, p.ajuste_vendedor?.importe_original].filter(conValor).map(centavosDe);
  // El mismo renglón con otras fechas y otra etiqueta (la IA corrigió el corte): su importe y sus kWh, juntos.
  const parejas = (p: PeriodoConsumo): [number, number][] =>
    [[p.importe, p.consumo_kwh] as const, [p.ajuste_vendedor?.importe_original, p.ajuste_vendedor?.consumo_original_kwh] as const].flatMap(([i, k]) => (conValor(i) && conValor(k) ? [[centavosDe(i), k] as [number, number]] : []));
  pasar((n, p) => conValor(n.importe) && parejas(p).some(([i, k]) => i === centavosDe(n.importe!) && k === n.consumo_kwh), (n) => conValor(n.importe));
  pasar((n, p) => !p.fin && !!n.fin && conValor(n.importe) && delPeriodo(p).includes(centavosDe(n.importe)), (n) => conValor(n.importe));
  return out;
}

/** Lo que decía el recibo de ese periodo (si ya se había ajustado antes, el original de la primera vez). */
const originalDe = (p: PeriodoConsumo) => p.ajuste_vendedor?.consumo_original_kwh ?? p.consumo_kwh;

/** El tope de kWh de un periodo ajustado de esta lectura (ver TOPE_AJUSTE_VECES). */
export function topeAjusteKwh(l: LecturaRecibo): number {
  const alto = Math.max(0, ...l.historial.map(originalDe));
  return Math.max(TOPE_AJUSTE_VECES * alto, TOPE_AJUSTE_MINIMO_KWH_MES * (l.facturacion === 'bimestral' ? 2 : 1));
}

/** ADENDA z48-10 (Diego, 28-oct: «cuando estiman, la IA a veces no mueve los datos de kWh y de dinero»). Dos maneras en que un
 *  ajuste se aplicaba al historial y NO llegaba a la cotización, sin decir nada:
 *  · el periodo queda FUERA del año que se cotiza (ventanaDelAnio: el recibo bimestral trae 2 años y cuentan los 6 bimestres
 *    más recientes): el motor no lo usa;
 *  · el periodo viene DOS VECES en el historial (el mismo recibo subido dos veces, o el recibo y el historial impreso de
 *    otro): el motor usa uno solo, el que trae importe (periodos.ts, «sin repetidos»), y el ajustado ya no trae importe.
 *  Sus «gemelos»: los otros renglones completos del mismo mes de corte. */
export function gemelosDelPeriodo(l: LecturaRecibo, i: number): number[] {
  const min = l.facturacion === 'bimestral' ? 54 : 25;
  const completo = (x: PeriodoConsumo) => !(x.inicio && x.fin) || dia(x.fin) - dia(x.inicio) >= min;
  // mes del corte: el del fin (unos días antes: un corte del día 1 es del mes anterior); sin fechas, el último de su etiqueta
  const corte = (x: PeriodoConsumo) => (x.fin ? new Date((dia(x.fin) - 4) * DIA_MS).toISOString().slice(0, 7) : mesDelPeriodo(x));
  const p = l.historial[i];
  if (!p || !completo(p)) return [];
  const k = corte(p);
  return k ? l.historial.flatMap((x, j) => (j !== i && completo(x) && corte(x) === k ? [j] : [])) : [];
}

/** Índices del historial que el motor usa (el año que se cotiza). */
const delAnio = (l: LecturaRecibo) => new Set(ventanaDelAnio(l.historial, l.facturacion).dentro);

/** Por qué una propuesta de ajuste no sirve (lista vacía = sirve). La revisa el paquete IA y otra vez el servidor. */
export function revisarAjusteConsumos(c: ContenidoCotizacion, a: AjusteConsumosPropuesto): string[] {
  const l = c.lecturas[a.medidor];
  if (!l) return [`el medidor ${a.medidor} no tiene lectura`];
  const tope = topeAjusteKwh(l);
  const dentro = delAnio(l);
  const vistos = new Set<number>();
  const problemas: string[] = [];
  for (const m of a.meses) {
    const p = l.historial[m.indice];
    if (!Number.isInteger(m.indice) || !p) {
      problemas.push(`el periodo ${m.indice} no existe en el historial del medidor ${a.medidor}`);
      continue;
    }
    if (vistos.has(m.indice)) problemas.push(`${p.etiqueta} viene dos veces`);
    vistos.add(m.indice);
    if (m.etiqueta !== p.etiqueta || m.antes_kwh !== p.consumo_kwh) problemas.push(`${p.etiqueta}: el «antes» no es el del borrador (${kwh(p.consumo_kwh)})`);
    if (!Number.isFinite(m.despues_kwh) || m.despues_kwh < 0) problemas.push(`${p.etiqueta}: ${m.despues_kwh} kWh no es un consumo`);
    else if (m.despues_kwh > tope) problemas.push(`${p.etiqueta}: ${kwh(m.despues_kwh)} pasa del tope razonable de ${kwh(tope)} (${TOPE_AJUSTE_VECES} veces el ${l.facturacion === 'bimestral' ? 'bimestre' : 'mes'} más alto del recibo)`);
    else if (m.despues_kwh === m.antes_kwh) problemas.push(`${p.etiqueta}: no cambia`);
    // z48-10: lo que no movería la cotización no se aplica callado
    if (!dentro.has(m.indice)) problemas.push(`${p.etiqueta}: ${FUERA_DEL_ANIO}`);
    for (const j of gemelosDelPeriodo(l, m.indice))
      if (!a.meses.some((x) => x.indice === j) && l.historial[j]!.consumo_kwh !== m.despues_kwh) problemas.push(`${p.etiqueta} viene dos veces en el historial (renglón ${j + 1}): se ajustan los dos o el cálculo se queda con el otro`);
  }
  return problemas;
}

export const FUERA_DEL_ANIO = 'queda fuera del año que se cotiza (es más viejo que los periodos del cálculo): cambiarlo no movería ni kWh ni pesos';
/** Marca de los avisos informativos de la curva (la pantalla de «Pídele a la IA» los muestra). */
export const PREFIJO_CURVA_SONORA = 'Curva de Sonora: ';

/** ADENDA z48-10: deja el ajuste listo para que SIEMPRE mueva kWh y pesos juntos (el pago de cada periodo ajustado lo estima
 *  el motor con las cuotas de CFE de su tarifa): quita los periodos fuera del año (y los nombra en `fuera`, para decirlo en
 *  rojo), ajusta también los gemelos con el mismo consumo, y en una CASA cambia los bloques planos (más de 3 periodos con el
 *  mismo consumo: «6 meses iguales y otros 6 iguales más bajos») por la curva medida de Sonora con el MISMO total (`curva`
 *  lo dice). En negocio no se toca la forma: su perfil compartido aquí es parejo (el del motor está en datos del motor). */
export function prepararAjuste(c: ContenidoCotizacion, a: AjusteConsumosPropuesto): { ajuste: AjusteConsumosPropuesto; fuera: string[]; curva: string | null } {
  const l = c.lecturas[a.medidor];
  if (!l) return { ajuste: a, fuera: [], curva: null };
  const dentro = delAnio(l);
  const fuera = a.meses.filter((m) => l.historial[m.indice] && !dentro.has(m.indice)).map((m) => m.etiqueta);
  let meses = a.meses.filter((m) => !l.historial[m.indice] || dentro.has(m.indice));
  for (const m of [...meses])
    for (const j of gemelosDelPeriodo(l, m.indice)) {
      const g = l.historial[j]!;
      if (!meses.some((x) => x.indice === j) && g.consumo_kwh !== m.despues_kwh) meses.push({ indice: j, etiqueta: g.etiqueta, antes_kwh: g.consumo_kwh, despues_kwh: m.despues_kwh });
    }
  let curva: string | null = null;
  const repetido = maximoRepetido(meses.map((m) => m.despues_kwh));
  if (perfilDe(c) === PERFIL_CONSUMO_CASA && meses.length >= 4 && repetido > 3) {
    const bimestral = l.facturacion === 'bimestral';
    const pesos = meses.map((m) => (l.historial[m.indice] ? pesoDelPeriodo(l.historial[m.indice]!, PERFIL_CONSUMO_CASA, bimestral) : null));
    if (pesos.every((w) => w !== null && w > 0)) {
      const total = meses.reduce((s, m) => s + m.despues_kwh, 0);
      const sw = pesos.reduce<number>((s, w) => s + (w ?? 0), 0);
      meses = meses.map((m, k) => ({ ...m, despues_kwh: Math.round((total * pesos[k]!) / sw) })).filter((m) => m.despues_kwh !== m.antes_kwh);
      curva = `${PREFIJO_CURVA_SONORA}la IA puso ${repetido} periodos con el mismo consumo (bloques); se repartieron con la curva de calor de Sonora con el mismo total (${kwh(total)}): sube en primavera, pico en julio–agosto, mínimo en invierno.`;
    }
  }
  return { ajuste: { ...a, meses: meses.sort((x, y) => x.indice - y.indice) }, fuera, curva };
}

/** Cuántas veces se repite el valor más repetido (redondeado a kWh): más de 3 = bloques planos, no una curva. */
export function maximoRepetido(valores: readonly number[]): number {
  const cuenta = new Map<number, number>();
  for (const v of valores) cuenta.set(Math.round(v), (cuenta.get(Math.round(v)) ?? 0) + 1);
  return Math.max(0, ...cuenta.values());
}

/** z25b: el motivo cuando el vendedor cambia los kWh a mano y no escribe por qué. */
export const MOTIVO_EDITADO_A_MANO = 'editado a mano';

const igual = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** z25d: lo que `ajuste_vendedor.recibo` guarda del periodo como lo trajo el recibo (ver PeriodoDelRecibo). */
function delRecibo(p: PeriodoConsumo): PeriodoDelRecibo {
  return {
    origen: p.origen,
    precio_medio: p.precio_medio,
    factor_carga_pct: p.factor_carga_pct,
    kwh_base: p.kwh_base,
    kwh_intermedia: p.kwh_intermedia,
    kwh_punta: p.kwh_punta,
    ...(p.cargos !== undefined ? { cargos: p.cargos } : {}),
    ...(p.precios_energia !== undefined ? { precios_energia: p.precios_energia } : {}),
  };
}

/** z25b (Diego, 20-oct: «cuando editas los kWh manual por mes o bimestre, no ajusta el importe a pagar, se queda en el
 *  importe pasado y está mal para el cálculo»). El periodo `despues` (como lo dejó quien edita) con el consumo distinto al
 *  de `antes`: todo lo que el recibo cobró o midió por EL CONSUMO VIEJO deja de valer. Importe, precio medio (con él, el
 *  motor cobraba los kWh nuevos al precio medio inflado de un mes bajo en GDMTO/GDMTH), cargos y factor de carga quedan en
 *  null, y el motor estima ese pago con las cuotas de CFE; los kWh por franja y por bloque se escalan; origen «manual» (una
 *  lectura nueva no lo pisa). La DEMANDA no cambia: editar los kWh no cambia lo que midió el medidor (z25d). En
 *  `ajuste_vendedor` queda TODO lo del recibo que se cambió o se puso en null (consumo, importe y `recibo`: precio medio,
 *  factor de carga, franjas, cargos, bloques y origen), de la PRIMERA vez. Lo que quien edita escribió en el mismo cambio
 *  (su importe, sus franjas, su ajuste_vendedor) se respeta. Si vuelve al consumo del recibo sin escribir importe, el
 *  periodo queda EXACTAMENTE como lo trajo el recibo y se quita la marca (z25d: antes se quedaba sin precio medio ni factor
 *  de carga y con origen «manual»). La usan el ajuste de la IA y aplicarEdiciones (toda edición de kWh). */
export function periodoConConsumoEditado(antes: PeriodoConsumo, despues: PeriodoConsumo, ajuste: { motivo: string; fuente: AjusteConsumoVendedor['fuente'] }): PeriodoConsumo {
  const factor = antes.consumo_kwh > 0 ? despues.consumo_kwh / antes.consumo_kwh : null;
  const escala = (x: number | null | undefined) => (x === null || x === undefined || factor === null ? null : Math.round(x * factor));
  const franja = (k: 'kwh_base' | 'kwh_intermedia' | 'kwh_punta') => (igual(despues[k], antes[k]) ? escala(antes[k]) : despues[k]);
  const suyo = <K extends keyof PeriodoConsumo>(k: K, si: PeriodoConsumo[K]) => (igual(despues[k], antes[k]) ? si : despues[k]);
  const escribioImporte = !igual(despues.importe, antes.importe);
  const escribioAjuste = !igual(despues.ajuste_vendedor, antes.ajuste_vendedor);
  // ADENDA z60 («Completar el año»): un periodo que estimó el cotizador (origen «estimado», sin IA) sigue ESTIMADO aunque el
  // vendedor le cambie los kWh (hoy83, punto 7 del diagnóstico de hoy82: quedaba «a mano» y el motor y el PDF lo contaban como
  // un recibo REAL). Si no venía de un recibo, no hay un «lo que decía el recibo» que guardar: sin ajuste_vendedor.
  const completado = antes.origen === 'estimado' && !antes.proyeccion_ia;
  if (completado && !antes.ajuste_vendedor) return { ...despues, importe: escribioImporte ? despues.importe : null, origen: 'estimado' };
  const original = antes.ajuste_vendedor?.consumo_original_kwh ?? antes.consumo_kwh;
  const importeOriginal = antes.ajuste_vendedor ? antes.ajuste_vendedor.importe_original : antes.importe;
  // Del recibo, de la PRIMERA vez: si ya estaba ajustado, lo que guardó ese ajuste (uno escrito a mano sin `recibo`: nada).
  const recibo = antes.ajuste_vendedor ? antes.ajuste_vendedor.recibo : delRecibo(antes);
  // z25e: volver al consumo del recibo restaura el periodo del recibo SIN IMPORTAR quién lo pida (vendedor, IA o IA
  // simulada). Antes solo el vendedor: si la IA proponía el consumo del recibo para un mes ya ajustado, el mes se quedaba
  // marcado con importe, precio medio y factor de carga en null y el pago de GDMTO caía 60 %. No cuenta como «escribió»
  // quitar la marca ni dejar el importe del recibo: es lo que trae el periodo ya restaurado cuando aplicarEdiciones lo
  // vuelve a pasar por aquí (con la marca de antes y el importe en null), y debe salir idéntico.
  const vuelveAlRecibo =
    !!antes.ajuste_vendedor &&
    despues.consumo_kwh === original &&
    (!escribioAjuste || despues.ajuste_vendedor == null) &&
    (!escribioImporte || igual(despues.importe, importeOriginal));
  if (vuelveAlRecibo) {
    if (recibo) {
      // Vuelve al recibo: lo que el ajuste guardó regresa tal cual (salvo lo que quien edita escribió en este mismo cambio).
      const { ajuste_vendedor: _quitado, cargos: _c, precios_energia: _b, ...resto } = despues;
      const deVuelta: PeriodoConsumo = {
        ...resto,
        importe: importeOriginal,
        // z60: un mes casi en cero que «Completar el año» estimó vuelve a ser del recibo aunque la tabla escriba «manual».
        origen: completado ? recibo.origen : suyo('origen', recibo.origen),
        precio_medio: suyo('precio_medio', recibo.precio_medio),
        factor_carga_pct: suyo('factor_carga_pct', recibo.factor_carga_pct),
        kwh_base: suyo('kwh_base', recibo.kwh_base),
        kwh_intermedia: suyo('kwh_intermedia', recibo.kwh_intermedia),
        kwh_punta: suyo('kwh_punta', recibo.kwh_punta),
      };
      const cargos = suyo('cargos', recibo.cargos);
      const bloques = suyo('precios_energia', recibo.precios_energia);
      return { ...deVuelta, ...(cargos !== undefined ? { cargos } : {}), ...(bloques !== undefined ? { precios_energia: bloques } : {}) };
    }
  }
  const base: PeriodoConsumo = {
    ...despues,
    kwh_base: franja('kwh_base'),
    kwh_intermedia: franja('kwh_intermedia'),
    kwh_punta: franja('kwh_punta'),
    precio_medio: suyo('precio_medio', null),
    factor_carga_pct: suyo('factor_carga_pct', null),
    ...(despues.cargos ? { cargos: suyo('cargos', null) } : {}),
    ...(despues.precios_energia?.length && igual(despues.precios_energia, antes.precios_energia) ? { precios_energia: despues.precios_energia.map((b) => ({ ...b, kwh: escala(b.kwh) })) } : {}),
    // ADENDA z60: «Completar el año» estima un mes del recibo casi en cero (servicio nuevo): queda «estimado» (el motor y el PDF
    // lo cuentan como estimado) con lo que decía el recibo en ajuste_vendedor; si después el vendedor lo cambia a otro número que
    // no es el del recibo, sigue «estimado» (hoy83). Cualquier otro cambio de kWh, «a mano».
    origen: (despues.origen === 'estimado' && antes.origen !== 'estimado') || completado ? 'estimado' : 'manual',
  };
  if (vuelveAlRecibo) {
    const { ajuste_vendedor: _quitado, ...sinMarca } = base;
    return { ...sinMarca, importe: importeOriginal };
  }
  return {
    ...base,
    importe: escribioImporte ? despues.importe : null,
    ajuste_vendedor: escribioAjuste
      ? despues.ajuste_vendedor
      : { consumo_original_kwh: original, importe_original: importeOriginal, motivo: ajuste.motivo.trim().slice(0, 400) || MOTIVO_EDITADO_A_MANO, fuente: ajuste.fuente, ...(recibo ? { recibo } : {}) },
  };
}

/** Las ediciones que aplican el ajuste (una por periodo): consumo nuevo, origen «manual» (una lectura nueva no lo pisa),
 *  el original en `ajuste_vendedor` y lo que cobró el recibo por el consumo viejo en null (ver periodoConConsumoEditado). */
export function edicionesDeAjuste(c: ContenidoCotizacion, a: AjusteConsumosPropuesto, fuente: AjusteConsumoVendedor['fuente']): EdicionCampo[] {
  const l = c.lecturas[a.medidor];
  if (!l) return [];
  const motivo = a.justificacion.slice(0, 300);
  return a.meses.flatMap((m): EdicionCampo[] => {
    const p = l.historial[m.indice];
    if (!p) return [];
    const nuevo = periodoConConsumoEditado(p, { ...p, consumo_kwh: m.despues_kwh }, { motivo, fuente });
    return [{ op: 'reemplazar', ruta: `/lecturas/${a.medidor}/historial/${m.indice}`, antes: p, despues: nuevo, motivo }];
  });
}

/** Para el historial de la cotización: qué consumos cambió una edición por un ajuste del vendedor, y por qué. */
export function textoAjusteAplicado(antes: ContenidoCotizacion, despues: ContenidoCotizacion): string | null {
  const partes: string[] = [];
  for (const M of ['A', 'B'] as const) {
    const n = despues.lecturas[M];
    if (!n) continue;
    const viejos = antes.lecturas[M]?.historial ?? [];
    const cambiados = n.historial.filter((p, i) => p.ajuste_vendedor && (viejos[i]?.consumo_kwh !== p.consumo_kwh || !viejos[i]?.ajuste_vendedor));
    if (!cambiados.length) continue;
    const quien = cambiados[0]!.ajuste_vendedor!.fuente === 'ia_simulada' ? 'con la IA SIMULADA' : cambiados[0]!.ajuste_vendedor!.fuente === 'ia' ? 'con la IA' : 'a mano';
    const lista = cambiados.map((p) => `${p.etiqueta} ${numero(p.ajuste_vendedor!.consumo_original_kwh)} → ${kwh(p.consumo_kwh)}`).join('; ');
    partes.push(
      `Ajustó ${quien} el consumo de ${cambiados.length === 1 ? '1 periodo' : `${cambiados.length} periodos`} del medidor ${M}: ${lista}. Por qué: ${cambiados[0]!.ajuste_vendedor!.motivo} Esos periodos guardan el consumo del recibo y ya no usan lo que cobró CFE.`,
    );
  }
  return partes.length ? partes.join(' ') : null;
}

// ───────────────────────────── Regla sin IA (IA simulada) ─────────────────────────────

const PIDE_AJUSTE: readonly [RegExp, string][] = [
  [/construccion|construyendo|obra negra|remodelacion/, 'casa en construcción'],
  [/no viv[i]?an|no vivia|no habitab|deshabitad|desocupad/, 'no vivían ahí'],
  [/casa nueva|recien (?:se )?mud|se mudaron|se cambiaron/, 'recién se mudaron'],
  [/no (?:son|eran) representativ|consumos? (?:muy )?baj/, 'consumos que no representan el uso de hoy'],
];
const EQUIPOS: readonly [RegExp, string][] = [
  [/(?:carro|auto|coche|vehiculo) electrico/, 'carro eléctrico'],
  [/estufa (?:electrica|de induccion)|parrilla de induccion/, 'estufa eléctrica'],
  [/calenton|boiler|calentador/, 'calentón'],
  [/minisplit|mini split|aire acondicionado|aires|clima/, 'aires'],
  [/alberca|bomba/, 'alberca'],
  [/secadora/, 'secadora'],
];
const MES_TEXTO = `(${NOMBRES.join('|')}|setiembre|ene|feb|mar|abr|may|jun|jul|ago|sep|sept|oct|nov|dic)`;
const DESDE = new RegExp(`(?:a partir de|desde)\\s+(?:el\\s+)?(?:mes\\s+de\\s+)?${MES_TEXTO}\\b\\.?(?:\\s+(?:de(?:l)?\\s+)?(\\d{4}|\\d{2})\\b)?`);

export type AjustePorReglas = { ajuste: AjusteConsumosPropuesto; pregunta?: undefined } | { ajuste?: undefined; pregunta: string };

/** IA SIMULADA para «casa en construcción / no vivían / a partir de <mes>»: los periodos anteriores a ese mes se suben
 *  al consumo de los meses reales (desde ese mes) repartido con la estacionalidad (calor de Sonora en casa, parejo en
 *  negocio). Solo SUBE: un mes que ya era más alto que lo estimado se queda. null = la instrucción no es de esta clase. */
export function ajustePorReglas(instruccion: string, c: ContenidoCotizacion): AjustePorReglas | null {
  const t = instruccion.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  const razon = PIDE_AJUSTE.find(([r]) => r.test(t))?.[1];
  if (!razon) return null;
  const M = /medidor\s*b\b/.test(t) ? 'B' : 'A';
  const l = c.lecturas[M];
  if (!l || l.historial.length < 2) return { pregunta: `El medidor ${M} no tiene historial de consumos que ajustar: sube o lee primero los recibos.` };
  const d = DESDE.exec(t);
  // «casa nueva, 450 kWh al mes» sin mes es de las reglas de las notas (estimación de cliente nuevo), no de esta.
  if (!d) return razon === 'recién se mudaron' ? null : { pregunta: '¿Desde qué mes el consumo ya es el real? Escríbelo así: «a partir de junio 2026».' };
  const nombre = d[1]!.slice(0, 3);
  const mes0 = NOMBRES.findIndex((n) => n.startsWith(nombre === 'set' ? 'sep' : nombre));
  const filas = l.historial.map((p, i) => ({ p, i, k: mesDelPeriodo(p) })).filter((x): x is { p: PeriodoConsumo; i: number; k: string } => x.k !== null);
  let objetivo: string | undefined;
  if (d[2]) {
    const a = Number(d[2]);
    objetivo = clave(a < 100 ? 2000 + a : a, mes0);
  } else objetivo = filas.filter((x) => Number(x.k.slice(5, 7)) - 1 === mes0).map((x) => x.k).sort().at(-1);
  if (!objetivo) return { pregunta: `No hay un periodo de ${NOMBRES[mes0]} en el historial del medidor ${M}: ¿de qué año?` };
  const reales = filas.filter((x) => x.k >= objetivo).sort((a, b) => a.k.localeCompare(b.k));
  const bajos = filas.filter((x) => x.k < objetivo).sort((a, b) => a.k.localeCompare(b.k));
  const etiquetaObjetivo = `${MESES[mes0]} ${objetivo.slice(2, 4)}`;
  if (!reales.length) return { pregunta: `No hay periodos desde ${etiquetaObjetivo} en el historial del medidor ${M}: ¿desde qué mes el consumo es el real?` };
  if (!bajos.length) return { pregunta: `Todos los periodos del medidor ${M} son desde ${etiquetaObjetivo}: no hay meses anteriores que ajustar.` };

  const perfil = perfilDe(c);
  const bimestral = l.facturacion === 'bimestral';
  const conPeso = reales.map((x) => ({ ...x, w: pesoDelPeriodo(x.p, perfil, bimestral) })).filter((x) => x.w !== null && x.w > 0);
  if (!conPeso.length) return { pregunta: `No pude ubicar en el calendario los periodos desde ${etiquetaObjetivo}: corrígelos a mano en la tabla.` };
  const anual = conPeso.reduce((s, x) => s + x.p.consumo_kwh, 0) / conPeso.reduce((s, x) => s + x.w!, 0);
  const meses: MesAjustado[] = [];
  let seQuedan = 0;
  for (const x of bajos) {
    const w = pesoDelPeriodo(x.p, perfil, bimestral);
    if (w === null) continue;
    const est = Math.round(anual * w);
    if (est > x.p.consumo_kwh) meses.push({ indice: x.i, etiqueta: x.p.etiqueta, antes_kwh: x.p.consumo_kwh, despues_kwh: est });
    else seQuedan++;
  }
  if (!meses.length) return { pregunta: `Los periodos antes de ${etiquetaObjetivo} ya son iguales o más altos que lo que da la estimación: no hay nada que subir.` };
  // z48-10: solo lo que mueve la cotización (el año que se cotiza, con sus renglones repetidos)
  const listo = prepararAjuste(c, { medidor: M, justificacion: '-', supuestos: [], meses });
  if (!listo.ajuste.meses.length) return { pregunta: `Los periodos antes de ${etiquetaObjetivo} (${listo.fuera.join(', ')}) ${FUERA_DEL_ANIO}.` };
  meses.splice(0, meses.length, ...listo.ajuste.meses);
  const promedio = reales.reduce((s, x) => s + x.p.consumo_kwh, 0) / reales.length;
  const equipos = EQUIPOS.filter(([r]) => r.test(t)).map(([, n]) => n);
  const supuestos = [
    `Meses reales: ${reales[0]!.p.etiqueta} a ${reales.at(-1)!.p.etiqueta} (${reales.length} ${reales.length === 1 ? 'periodo' : 'periodos'}, ${kwh(Math.round(promedio))} en promedio).`,
    `No representan el uso de hoy (${razon}): ${bajos[0]!.p.etiqueta} a ${bajos.at(-1)!.p.etiqueta}.`,
    perfil === PERFIL_CONSUMO_CASA
      ? 'Cada mes ajustado = el consumo de los meses reales repartido con el calor de Sonora (perfil de consumo de casa del cotizador).'
      : 'Cada mes ajustado = el consumo de los meses reales repartido parejo en el año (negocio).',
    ...(equipos.length ? [`Los equipos que dijiste (${equipos.join(', ')}) ya se ven en los meses reales: no sumé nada aparte.`] : []),
    ...(seQuedan ? [`Solo subo meses: ${seQuedan === 1 ? '1 periodo ya era' : `${seQuedan} periodos ya eran`} más alto que lo estimado y se queda igual.`] : []),
    ...(listo.fuera.length ? [`No ajusto ${listo.fuera.join(', ')}: fuera del año que se cotiza (no mueve la cotización).`] : []),
    'No cambio tarifa, facturación ni datos del titular.',
  ];
  return {
    ajuste: {
      medidor: M,
      justificacion: `${razon[0]!.toUpperCase()}${razon.slice(1)}: el consumo real empieza en ${reales[0]!.p.etiqueta}; los periodos anteriores se estiman con los meses reales y la estacionalidad.`.slice(0, 400),
      supuestos: supuestos.slice(0, 8),
      meses,
    },
  };
}
